import React, { useState, useEffect, useRef, useCallback } from 'react'
import Typography from '../../Typography'
import styles from './DownloadImages.module.sass'
import { useParams } from 'react-router-dom'
import Order from '../../../models/Order'
import PreviewImage from '../../PreviewImage'
import Button from '../../Button'
import Spinner from '../../Spinner'
import { countReady } from '../../../utils/downloadReadyCount'

const useOrder = (orderId, email) => {
  const [order, setOrder] = useState([])

  useEffect(() => {
    Order
      .includes({
        cart: {
          orderPackages: [
            'background',
            'package',
            'student',
            'studentImage',
            { sheets: ['pose', 'background', 'foreground'] },
            { addonSheets: ['pose', 'background'] },
            { api_option: 'image_types' },
            { extras: 'prices' },
          ],
        },
      })
      .where({ for_email: [orderId, email] })
      .first()
      .then((response) => setOrder(response.data))
  }, [orderId, email])

  return order;
};

const buildDownloadableImages = (order) => {
  const images = []
  if (!order || !order.cart || !order.cart.orderPackages) return images

  order.cart.orderPackages.forEach((orderPackage) => {
    if (!orderPackage.hasDownloadableImages) return

    if (orderPackage.sheets && orderPackage.sheets.length > 0) {
      orderPackage.sheets.filter((sheet) => sheet.hasDownloadableImage).forEach((sheet) => {
        if (!sheet.pose) return
        images.push({
          type: 'pose',
          id: sheet.pose.id,
          imageUrl: sheet.pose.imageUrl,
          backgroundUrl: sheet.background && sheet.background.imageUrl,
          foregroundUrl: sheet.foregroundId ? sheet.foreground?.imageUrl : undefined,
          isPNG: sheet.pose.isPNG(),
          studentId: orderPackage.student.id,
          orderPackageId: orderPackage.id,
          backgroundId: sheet.background && sheet.background.id,
          foregroundId: sheet.foregroundId,
          poseNumber: sheet.pose.number,
          backgroundName: sheet.background && sheet.background.color,
          itemKey: `pose-${sheet.pose.id}`,
        })
      })
    }

    if (orderPackage.addonSheets && orderPackage.addonSheets.length > 0) {
      orderPackage.addonSheets.filter((sheet) => sheet.hasDownloadableImage).forEach((sheet) => {
        if (!sheet.pose) return
        images.push({
          type: 'pose',
          id: sheet.pose.id,
          imageUrl: sheet.pose.imageUrl,
          backgroundUrl: sheet.background && sheet.background.imageUrl,
          isPNG: sheet.pose.isPNG(),
          studentId: orderPackage.student.id,
          orderPackageId: orderPackage.id,
          backgroundId: sheet.background && sheet.background.id,
          poseNumber: sheet.pose.number,
          backgroundName: sheet.background && sheet.background.color,
          itemKey: `pose-${sheet.pose.id}`,
        })
      })
    }

    if (orderPackage.studentImage && orderPackage.studentImage.imageUrl) {
      images.push({
        type: 'student_image',
        id: orderPackage.studentImage.id,
        imageUrl: orderPackage.studentImage.imageUrl,
        backgroundUrl: orderPackage.background && orderPackage.background.imageUrl,
        isPNG: orderPackage.studentImage.isPNG() && !!orderPackage.backgroundId,
        studentId: orderPackage.student.id,
        orderPackageId: orderPackage.id,
        backgroundId: orderPackage.background && orderPackage.background.id,
        poseNumber: null,
        backgroundName: orderPackage.background && orderPackage.background.color,
        itemKey: `student_image-${orderPackage.studentImage.id}`,
      })
    }
  })

  return images
}

const buildItemsList = (images) => {
  return images.map((img) => ({
    type: img.type,
    id: img.id,
    background_id: img.backgroundId,
    foreground_id: img.foregroundId,
    order_package_id: img.orderPackageId,
  }))
}

const getDownloadUrl = (image, email) => {
  if (!image) return ''

  // Always use download_png_image for full resolution composited output
  const baseUrl = '/download/download_png_image'
  if (image.type === 'pose') {
    return `${baseUrl}/${image.studentId}?pose_id=${image.id}&background_id=${image.backgroundId}&order_package_id=${image.orderPackageId}&email=${encodeURIComponent(email)}${image.foregroundId ? `&foreground_id=${image.foregroundId}` : ''}`
  } else {
    return `${baseUrl}/${image.studentId}?student_image_id=${image.id}&background_id=${image.backgroundId}&order_package_id=${image.orderPackageId}&email=${encodeURIComponent(email)}`
  }
}

const DownloadImages = () => {
  // Support both param formats: /download-image/orderId/:orderId/email/:email AND /order/download_images/:order_id/:email
  const params = useParams();
  const orderId = params.order_id || params.orderId;
  const email = params.email;

  const order = useOrder(orderId, email);
  const [availability, setAvailability] = useState({})
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(true)
  const pollingRef = useRef(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const elapsedTimerRef = useRef(null)
  const workOrderCreatedRef = useRef(false)
  const [isDownloadingZip, setIsDownloadingZip] = useState(false)
  const [zipProgress, setZipProgress] = useState(null)
  const zipPollingRef = useRef(null)

  const [downloadableImages, setDownloadableImages] = useState([])

  useEffect(() => {
    if (order && order.cart) {
      const images = buildDownloadableImages(order)
      setDownloadableImages(images)
      // If the order has no downloadable images at all (print-only order),
      // clear the loading spinner so the empty-state message renders.
      if (images.length === 0) {
        setIsCheckingAvailability(false)
      }
    }
  }, [order])

  const checkAvailability = useCallback((items) => {
    fetch('/download/check_image_availability', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order_id: orderId, email, items }),
    })
      .then((res) => res.json())
      .then((data) => {
        setAvailability(data.availability || {})
        setIsCheckingAvailability(false)

        const allReady = Object.values(data.availability || {}).every((v) => v === true)
        if (allReady && pollingRef.current) {
          clearTimeout(pollingRef.current)
          pollingRef.current = null
        }
      })
      .catch(() => {
        setIsCheckingAvailability(false)
      })
  }, [orderId, email])

  const createWorkOrder = useCallback(() => {
    if (workOrderCreatedRef.current) return
    workOrderCreatedRef.current = true

    const unavailableItems = downloadableImages
      .filter((img) => availability[img.itemKey] !== true)
      .map((img) => ({ type: img.type, id: img.id }))

    const orderPackageIds = downloadableImages
      .filter((img) => availability[img.itemKey] !== true)
      .map((img) => img.orderPackageId)

    fetch('/download/create_download_work_order', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        order_id: orderId,
        email,
        unavailable_items: unavailableItems,
        order_package_ids: orderPackageIds,
      }),
    }).catch(() => {})
  }, [orderId, email, downloadableImages, availability])

  useEffect(() => {
    if (downloadableImages.length === 0) return

    const items = buildItemsList(downloadableImages)
    const startedAt = Date.now()
    let cancelled = false

    // Tight polling for the first 90s — most orders (≤4 images) complete here
    // and we want "Ready" to flip on within ~5s of the file landing on S3.
    // After 90s, back off to 30s for long-running orders (Buy All Digital, ~12
    // images) so we don't hammer the endpoint.
    const scheduleNext = () => {
      if (cancelled) return
      const elapsed = Date.now() - startedAt
      const nextDelay = elapsed < 90000 ? 4000 : 30000
      pollingRef.current = setTimeout(() => {
        if (cancelled) return
        checkAvailability(items)
        scheduleNext()
      }, nextDelay)
    }

    checkAvailability(items)
    scheduleNext()

    return () => {
      cancelled = true
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [downloadableImages, checkAvailability])

  // Elapsed time tracker for unavailable images
  useEffect(() => {
    if (isCheckingAvailability || downloadableImages.length === 0) return

    const allReady = downloadableImages.length > 0 && countReady(downloadableImages, availability) === downloadableImages.length

    if (allReady) {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
      setElapsedSeconds(0)
      return
    }

    if (!elapsedTimerRef.current) {
      elapsedTimerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (elapsedTimerRef.current) {
        clearInterval(elapsedTimerRef.current)
        elapsedTimerRef.current = null
      }
    }
  }, [isCheckingAvailability, availability, downloadableImages])

  // The "Delayed" threshold scales with order size:
  //   ≤4 images → 60s (the customer-visible goal)
  //   >4 images → 30s + 8s × N, capped at 180s
  // Mid-state "Preparing… (warning color)" kicks in at half this threshold.
  // Threshold also gates the auto-WorkOrder creation so production doesn't
  // get a "delayed download" ticket while EC2 is mid-render on a 12-image
  // Buy All Digital order.
  const delayedThresholdSec = downloadableImages.length <= 4
    ? 60
    : Math.min(180, 30 + downloadableImages.length * 8)
  const warnThresholdSec = Math.floor(delayedThresholdSec / 2)

  useEffect(() => {
    if (elapsedSeconds >= delayedThresholdSec && !workOrderCreatedRef.current) {
      createWorkOrder()
    }
  }, [elapsedSeconds, delayedThresholdSec, createWorkOrder])

  const handleDownloadAll = async () => {
    setIsDownloadingZip(true)
    setZipProgress(null)

    try {
      const imagesSpec = downloadableImages.map((img) => ({
        type: img.type,
        id: img.id,
        background_id: img.backgroundId,
        foreground_id: img.foregroundId,
        order_package_id: img.orderPackageId,
      }))

      const response = await fetch('/download/download_all_images', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId, email, images: imagesSpec }),
      })

      if (!response.ok) throw new Error('Download failed')
      const { export_id } = await response.json()

      // Poll for ZIP completion
      zipPollingRef.current = setInterval(async () => {
        try {
          const statusRes = await fetch(`/download/zip_status?export_id=${export_id}`)
          const status = await statusRes.json()
          setZipProgress(status.progress)

          if (status.ready) {
            clearInterval(zipPollingRef.current)
            zipPollingRef.current = null
            setIsDownloadingZip(false)
            setZipProgress(null)
            window.location.href = `/download/customer_zip?export_id=${export_id}`
          }
        } catch (e) {
          console.error('ZIP status check failed:', e)
        }
      }, 2000)
    } catch (e) {
      console.error('ZIP download failed:', e)
      setIsDownloadingZip(false)
    }
  }

  // Cleanup ZIP polling on unmount
  useEffect(() => {
    return () => {
      if (zipPollingRef.current) {
        clearInterval(zipPollingRef.current)
      }
    }
  }, [])

  if (typeof order !== 'object') return null
  if (!order) return null

  const totalCount = downloadableImages.length
  const readyCount = countReady(downloadableImages, availability)
  const allReady = totalCount > 0 && readyCount === totalCount

  if (totalCount === 0 && !isCheckingAvailability) {
    return (
      <div className={styles.container}>
        <Typography align="center" weight={500}>IMAGE DOWNLOAD</Typography>
        <Typography align="center" weight={600}>Order #{order.id}</Typography>
        <div className={styles.emptyState}>
          This order does not include any digital downloads — it's a print-only order.
          If you believe this is a mistake, please contact us for assistance.
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Typography align="center" weight={500}>IMAGE DOWNLOAD</Typography>
      <Typography align="center" weight={600}>Order #{order.id}</Typography>

      {totalCount > 0 && (
        <div className={styles.progressContainer}>
          {isCheckingAvailability ? (
            <div className={styles.progressLabel}>Checking image availability...</div>
          ) : allReady ? (
            <div className={styles.allReady}>All {totalCount} images ready for download</div>
          ) : (
            <>
              <div className={styles.progressLabel}>{readyCount} of {totalCount} images ready</div>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${(readyCount / totalCount) * 100}%` }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {allReady && totalCount > 1 && (
        <div className={styles.downloadAllContainer}>
          <button
            className={styles.downloadAllButton}
            onClick={handleDownloadAll}
            disabled={isDownloadingZip}
          >
            {isDownloadingZip
              ? (zipProgress && zipProgress.total_images
                ? `Creating Zip File... ${zipProgress.images_processed || 0}/${zipProgress.total_images}`
                : 'Creating Zip File...')
              : 'Download All (Zip File)'}
          </button>
        </div>
      )}

      {isCheckingAvailability && <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>}

      {!isCheckingAvailability && (
        <div className={styles.imageGrid}>
          {downloadableImages.map((image) => {
            const isReady = availability[image.itemKey] === true
            const downloadUrl = getDownloadUrl(image, email)
            const poseLabel = image.poseNumber != null ? `Pose ${String(image.poseNumber).padStart(2, '0')}` : 'Image'

            return (
              <div key={image.itemKey} className={styles.gridCell}>
                <div className={styles.previewWrapper}>
                  <PreviewImage
                    actualForegroundUrl={image.foregroundUrl}
                    foregroundUrl={image.imageUrl}
                    backgroundUrl={image.backgroundUrl || ''}
                    accessCodeOnly={false}
                  />
                </div>
                <div className={styles.imageLabel}>{poseLabel}</div>
                {image.backgroundName && (
                  <div className={styles.bgLabel}>{image.backgroundName}</div>
                )}
                <div className={styles.downloadButtonWrapper}>
                  {isReady ? (
                    <a href={downloadUrl} className={styles.downloadLink}>
                      <Button fullWidth variant="primary">Download</Button>
                    </a>
                  ) : elapsedSeconds >= delayedThresholdSec ? (
                    <div className={`${styles.statusBar} ${styles.statusDelayed}`}>Delayed</div>
                  ) : elapsedSeconds >= warnThresholdSec ? (
                    <div className={`${styles.statusBar} ${styles.statusWarning}`}>Preparing...</div>
                  ) : (
                    <Button fullWidth variant="primary" disabled>Preparing...</Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
};

export default DownloadImages;
